/**
 * Locale-specific formatting helpers shared cross web + mobile + API.
 */

/** Format VND amount: 80000 → "80.000đ", 1200000 → "1.200.000đ" */
export function formatVnd(amount: number | bigint | string): string {
  const n =
    typeof amount === "bigint"
      ? Number(amount)
      : typeof amount === "string"
        ? Number(amount)
        : amount;
  if (!Number.isFinite(n)) return "0đ";
  return `${Math.round(n).toLocaleString("vi-VN")}đ`;
}

/** Label shown when a posting's salary is left open / negotiable. */
export const SALARY_NEGOTIABLE_LABEL = "Thoả thuận";

/**
 * A posting whose `salaryMax` is 0 (or missing/non-positive) means the
 * employer left the pay open → render as "Thoả thuận" everywhere instead of
 * "0đ". Shared rule across web, web-employer, admin, and mobile.
 */
export function isNegotiableSalary(max: number | bigint | string): boolean {
  const n = typeof max === "bigint" ? Number(max) : Number(max);
  return !Number.isFinite(n) || n <= 0;
}

/** Format salary range "6.000.000đ – 8.000.000đ /tháng", or "Thoả thuận". */
export function formatSalaryRange(
  min: number | bigint | string,
  max: number | bigint | string,
  suffix: string,
): string {
  if (isNegotiableSalary(max)) return SALARY_NEGOTIABLE_LABEL;
  const lo = formatVnd(min);
  const hi = formatVnd(max);
  if (lo === hi) return `${lo} ${suffix}`;
  return `${lo} – ${hi} ${suffix}`;
}

/** Compute age từ birthYear (chỉ năm để bảo vệ privacy ngày sinh chính xác). */
export function computeAge(birthYear: number, now: Date = new Date()): number {
  return now.getFullYear() - birthYear;
}

/** Distance Haversine (km) — BE compute, FE chỉ format. */
export function formatKm(km: number): string {
  if (km < 1) return `${Math.round(km * 1000)} mét`;
  if (km < 10) return `${km.toFixed(1)} km`;
  return `${Math.round(km)} km`;
}

/** "vừa xong" / "5 phút trước" / "2 giờ trước" / "3 ngày trước" */
export function formatRelativeVi(
  date: Date | string,
  now: Date = new Date(),
): string {
  const d = typeof date === "string" ? new Date(date) : date;
  const diff = (now.getTime() - d.getTime()) / 1000;
  if (diff < 60) return "vừa xong";
  if (diff < 3600) return `${Math.floor(diff / 60)} phút trước`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} giờ trước`;
  if (diff < 86400 * 30) return `${Math.floor(diff / 86400)} ngày trước`;
  if (diff < 86400 * 365) return `${Math.floor(diff / 86400 / 30)} tháng trước`;
  return `${Math.floor(diff / 86400 / 365)} năm trước`;
}

/** Mask phone "+8490xxxxx12" (giữ 2 số đầu mã vùng + 2 số cuối) */
export function maskPhone(phone: string): string {
  const cleaned = phone.replace(/\s/g, "");
  if (cleaned.length < 6) return cleaned;
  const head = cleaned.slice(0, 4);
  const tail = cleaned.slice(-2);
  return `${head}${"x".repeat(Math.max(cleaned.length - 6, 4))}${tail}`;
}

/** Mask fullName "Nguyễn Văn A" → "Bạn A." */
export function maskFullName(fullName: string): string {
  const parts = fullName.trim().split(/\s+/);
  const last = parts[parts.length - 1];
  if (!last || last.length === 0) return "Ứng viên";
  const firstChar = last[0]!.toUpperCase();
  return `Bạn ${firstChar}.`;
}
